import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageShell from './PageShell';
import { api } from '../lib/api';
import { EXAMEN_GRILLE_31, EXAMEN_CATEGORIES } from '../data/examenBlancGrille31';
import { CRITERE_ETAPES_MAP } from '../data/critereEtapesMap';
import EtapeRemcCard, { findEtape } from '../components/EtapeRemcCard';
import {
  Calendar, User, AlertCircle, CheckCircle2, XCircle, AlertTriangle,
  Octagon, Trophy, Loader2, GraduationCap, ClipboardCheck, Lightbulb,
  Printer, ArrowRight, Sparkles, TrendingUp, ListChecks,
} from 'lucide-react';

const STATE_META = {
  ok:         { l: 'OK',          cls: 'bg-emerald-600 text-white',   Icon: CheckCircle2 },
  ameliorer:  { l: 'À améliorer', cls: 'bg-amber-500 text-amber-950', Icon: AlertTriangle },
  non_acquis: { l: 'Non acquis',  cls: 'bg-red-600 text-white',       Icon: XCircle },
};

const VERDICT_META = {
  favorable: { color: 'border-emerald-600/50 bg-emerald-950/25 text-emerald-300', Icon: Trophy, headline: 'Avis favorable — proche du niveau examen 🎉' },
  reserve:   { color: 'border-sky-600/50 bg-sky-950/25 text-sky-300',             Icon: AlertTriangle, headline: 'Avis réservé — encore un peu de travail' },
  ajourne:   { color: 'border-amber-600/50 bg-amber-950/25 text-amber-300',       Icon: XCircle, headline: 'Ajourné — niveau insuffisant, on retravaille !' },
  elim:      { color: 'border-red-600/60 bg-red-950/35 text-red-300',             Icon: Octagon, headline: 'Ajourné (éliminatoire) — priorité sécurité' },
  pending:   { color: 'border-white/10 bg-white/[0.03] text-white/60',            Icon: ClipboardCheck, headline: 'En cours d\'évaluation' },
};

export default function ExamenBlancPublicPage() {
  const { token } = useParams();
  const [exam, setExam] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const e = await api.publicMockExam(token);
        if (alive) setExam(e);
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || 'Examen introuvable ou expiré.');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (error) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto p-12 text-center">
          <AlertCircle className="mx-auto text-red-400 mb-4" size={32} />
          <h2 className="font-serif-editorial text-white text-[26px]">Fiche introuvable</h2>
          <p className="text-white/60 mt-2 text-[14px]">{error}</p>
          <Link to="/" className="inline-block mt-6 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold">Retour à l&apos;accueil</Link>
        </div>
      </PageShell>
    );
  }
  if (!exam) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto p-12 text-center text-white/50">
          <Loader2 className="animate-spin mx-auto mb-3" size={24} /> Chargement de ton examen blanc…
        </div>
      </PageShell>
    );
  }

  const dateFr = exam.date ? new Date(exam.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const stats = exam.stats || { ok: 0, ameliorer: 0, non_acquis: 0, elim: 0, total: 31 };
  const total = stats.total || 31;
  const okPct = exam.ok_pct != null ? exam.ok_pct : (total ? Math.round((stats.ok / total) * 100) : 0);
  const vMeta = VERDICT_META[exam.verdict_kind] || VERDICT_META.pending;
  const VIcon = vMeta.Icon;
  const grid = exam.grid || {};
  const elim = exam.elim || {};

  // Compile "critères à retravailler"
  const critereRows = EXAMEN_GRILLE_31.map((r) => ({
    ...r,
    state: grid[String(r.n)] || null,
    isElim: !!elim[String(r.n)],
  }));
  const toRework = critereRows.filter((r) => r.state === 'non_acquis' || r.state === 'ameliorer' || r.isElim);

  // Agréger les étapes de progression à retravailler
  const etapesSet = new Set();
  toRework.forEach((r) => {
    (CRITERE_ETAPES_MAP[r.n] || []).forEach((id) => etapesSet.add(id));
  });
  const etapesIds = Array.from(etapesSet);
  // Ordonner par phase (M avant O avant P) puis numéro
  const etapesToRework = etapesIds
    .map((id) => findEtape(id))
    .filter(Boolean)
    .sort((a, b) => {
      const order = { Mécanique: 0, Observation: 1, 'Préparation permis': 2 };
      const pa = order[a.phase] ?? 9;
      const pb = order[b.phase] ?? 9;
      if (pa !== pb) return pa - pb;
      return a.id.localeCompare(b.id, undefined, { numeric: true });
    });

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 lg:px-6 py-8 sm:py-12" data-testid="exam-public">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6 no-print">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Examen blanc</span>
        </nav>

        {/* Hero */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 opacity-[0.06] pointer-events-none">
            <GraduationCap size={220} strokeWidth={1} />
          </div>
          <div className="relative">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-mono-spec font-bold">Examen blanc · Grille 31 critères</div>
            <h1 className="mt-2 font-serif-editorial text-white text-[28px] sm:text-[34px] leading-tight">Bonjour {exam.student_name}</h1>
            <p className="mt-2 text-white/65 text-[14px]">Voici le récapitulatif de ton examen blanc avec <span className="text-white font-semibold">{exam.teacher_name}</span>.</p>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[12.5px] text-white/70">
              {exam.date && <span className="inline-flex items-center gap-1.5"><Calendar size={12} className="text-white/40" /> {dateFr}</span>}
              {exam.teacher_code && <span className="inline-flex items-center gap-1.5 no-print"><User size={12} className="text-white/40" /> Enseignant : <span className="font-mono-spec text-red-300">{exam.teacher_code}</span></span>}
            </div>
          </div>
        </div>

        {/* Verdict grand format */}
        <div className={`mt-6 rounded-2xl border-2 p-6 ${vMeta.color}`} data-testid="exam-verdict">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
              <VIcon size={26} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold opacity-90">Verdict de la session</div>
              <div className="mt-1 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">{vMeta.headline}</div>
              <p className="mt-2 text-white/80 text-[13.5px] leading-relaxed">{exam.verdict}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-3.5 text-center col-span-2 sm:col-span-1">
            <div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Score</div>
            <div className="font-serif-editorial text-white text-[26px] leading-none mt-1">{okPct}<span className="text-white/40 text-[15px]">%</span></div>
          </div>
          <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-emerald-300 font-bold font-mono-spec">OK</div>
            <div className="font-serif-editorial text-emerald-400 text-[26px] leading-none mt-1">{stats.ok}</div>
          </div>
          <div className="rounded-xl border border-amber-700/30 bg-amber-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">À améliorer</div>
            <div className="font-serif-editorial text-amber-400 text-[26px] leading-none mt-1">{stats.ameliorer}</div>
          </div>
          <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Non acquis</div>
            <div className="font-serif-editorial text-red-400 text-[26px] leading-none mt-1">{stats.non_acquis}</div>
          </div>
          <div className="rounded-xl border-2 border-red-700/50 bg-red-950/30 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Élim.</div>
            <div className="font-serif-editorial text-red-400 text-[26px] leading-none mt-1">{stats.elim}</div>
          </div>
        </div>

        {/* Barre de progression OK */}
        <div className="mt-4 rounded-xl border border-white/10 bg-[#0d0d0e] p-4">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-wider text-white/60 font-bold font-mono-spec">Progression sur les 31 critères</div>
            <div className="text-white/60 text-[11.5px] font-mono-spec">{stats.ok}/{total} OK</div>
          </div>
          <div className="h-3 rounded-full bg-white/10 overflow-hidden flex">
            <div className="h-full bg-emerald-500" style={{ width: `${(stats.ok / total) * 100}%` }} />
            <div className="h-full bg-amber-500" style={{ width: `${(stats.ameliorer / total) * 100}%` }} />
            <div className="h-full bg-red-500" style={{ width: `${(stats.non_acquis / total) * 100}%` }} />
          </div>
        </div>

        {/* Conseils enseignant */}
        {exam.conseils && (
          <div className="mt-6 rounded-xl border border-sky-500/30 bg-sky-950/15 p-5" data-testid="exam-conseils">
            <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2 inline-flex items-center gap-1.5"><Lightbulb size={12} /> Conseils de ton enseignant</div>
            <p className="text-white/85 text-[14px] leading-relaxed whitespace-pre-wrap">{exam.conseils}</p>
          </div>
        )}

        {/* Étapes de progression à retravailler — cœur de la fiche */}
        {etapesToRework.length > 0 && (
          <div className="mt-8" data-testid="exam-to-rework">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-mono-spec font-bold inline-flex items-center gap-1.5"><TrendingUp size={12} /> À retravailler dans ta progression</div>
            <h2 className="mt-2 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">
              {etapesToRework.length} étape{etapesToRework.length > 1 ? 's' : ''} à revoir
            </h2>
            <p className="mt-1.5 text-white/60 text-[13px]">
              Clique sur chaque étape pour découvrir <span className="text-emerald-300 font-semibold">pourquoi</span>, <span className="text-sky-300 font-semibold">comment</span> et <span className="text-red-300 font-semibold">quels risques</span>. Tu retrouveras ces étapes dans ta <Link to="/cours/progression" className="underline text-white hover:text-red-300">progression complète</Link>.
            </p>

            <div className="mt-5 space-y-2.5">
              {etapesToRework.map((etape) => (
                <EtapeRemcCard key={etape.id} etapeId={etape.id} defaultOpen={false} />
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/cours/progression"
                data-testid="exam-open-progression"
                className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[13px] font-bold shadow-lg shadow-emerald-950/40"
              >
                <TrendingUp size={13} /> Ouvrir Ma progression <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        )}

        {/* Détail intégral par catégorie (résumé compact) */}
        <div className="mt-10">
          <details className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden group" data-testid="exam-full-grid-toggle">
            <summary className="cursor-pointer px-4 py-4 flex items-center justify-between gap-3 hover:bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <ListChecks size={14} className="text-white/60" />
                <span className="text-white text-[13.5px] font-semibold">Voir le détail des 31 critères</span>
              </div>
              <span className="text-white/40 text-[11.5px] font-mono-spec group-open:hidden">Afficher</span>
              <span className="text-white/40 text-[11.5px] font-mono-spec hidden group-open:inline">Masquer</span>
            </summary>
            <div className="px-4 pb-4 space-y-3">
              {EXAMEN_CATEGORIES.map((cat) => {
                const rows = critereRows.filter((r) => r.cat === cat);
                return (
                  <div key={cat} className="rounded-xl border border-white/10 overflow-hidden">
                    <div className="px-3 py-2 bg-red-600/10 border-b border-red-700/30">
                      <div className="text-[11.5px] text-red-300 font-bold font-mono-spec uppercase tracking-wider">{cat}</div>
                    </div>
                    <ul className="divide-y divide-white/5">
                      {rows.map((r) => {
                        const meta = r.state ? STATE_META[r.state] : null;
                        return (
                          <li key={r.n} className="grid grid-cols-[32px_1fr_auto] gap-2 px-3 py-2.5 items-center" data-testid={`detail-critere-${r.n}`}>
                            <div className="text-white/45 font-mono-spec text-[10.5px] font-bold">N°{r.n}</div>
                            <div className="text-white/85 text-[12.5px] leading-snug">{r.label}</div>
                            <div className="flex flex-col items-end gap-1">
                              {meta ? (
                                <span className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-bold ${meta.cls}`}>
                                  <meta.Icon size={9} /> {meta.l}
                                </span>
                              ) : (
                                <span className="text-white/30 text-[10px] font-mono-spec">—</span>
                              )}
                              {r.isElim && (
                                <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-red-700 text-white text-[10px] font-bold">
                                  <Octagon size={9} /> Élim.
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </details>
        </div>

        <div className="hidden" data-marker="cta-signup"></div>

        {/* CTA discret compte */}
        <div className="mt-8 rounded-xl border border-white/10 bg-[#0d0d0e] p-4 no-print" data-testid="exam-cta-signup">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
              <Sparkles size={14} className="text-white/50" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white/85 text-[13px] font-semibold">Bonus (optionnel) · Continue de progresser</div>
              <p className="mt-0.5 text-white/50 text-[12px] leading-relaxed">Tu peux créer un compte gratuit pour retrouver ton historique et suivre ta progression. Pas obligatoire — ta fiche du jour est déjà ici.</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Link to={exam.teacher_code ? `/rejoindre?code=${exam.teacher_code}` : '/'} className="h-8 px-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/35 text-[11.5px] font-semibold inline-flex items-center gap-1.5">
                  Créer un compte
                </Link>
                <button onClick={() => window.print()} className="h-8 px-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/35 text-[11.5px] font-semibold inline-flex items-center gap-1.5">
                  <Printer size={11} /> Imprimer / PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-white/35 text-[11px] font-mono-spec">
          Examen blanc généré par {exam.teacher_name} · codevoiture.fr · Fiche consultée {exam.view_count} fois
        </p>
      </section>
    </PageShell>
  );
}
